import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import {
  ONE_OFF_CHANNEL_INFO,
  type SerializedAssetPublish,
  type SlotChannelGroup,
} from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../../common/types';
import type { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { downloadMediaUrl } from '../../inlineMediaUrl';
import { PublishBadges } from '../../publish/PublishBadges';
import { openPublishDialog } from '../../publish/publishDialogStore';
import { KitSlotView, slotChannelGroups, slotPublishes } from './kitSlots';
import { kitOutputLabel } from '../../kitLabels';
import { kitSlotLines } from './kitSlotLine';
import { kitSlotName } from './kitSlotName';
import { KitSlotThumb } from './KitSlotThumb';
import { KitSlotFoot, SlotChannel } from './KitSlotFoot';
import { KitStateChip } from './KitStateChip';
import { KitActions } from './useKitActions';
import { kitMessages } from './kitMessages';
import { KitMusicVideoFoot } from './KitMusicVideoFoot';
import { useKitSlotAdded } from './useKitSlotAdded';
import { RemoveAddedAssetDialog } from '../../addedAssets/RemoveAddedAssetDialog';
import { addedAssetsMessages } from '../../addedAssets/addedAssetsMessages';
import { sourceOfAsset } from '../../addedAssets/addedAssetFacts';
import { OnYouTubeBadge } from '../../addedAssets/OnYouTubeBadge';
import { KitHeaderProfileActions } from '../../profile/kit/KitHeaderProfileActions';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitSlotCardProps {
  accountId: number;
  item: CatalogItem;
  view: KitSlotView;
  publishes: readonly SerializedAssetPublish[];
  actions: KitActions;
  onOpen: (view: KitSlotView) => void;
  /** The kit asset uuids whose file has an old cover (`useCoverRedo`). */
  oldCoverUuids?: ReadonlySet<string>;
}

const FRAME_TEXT: Partial<Record<string, string>> = { lyricVideo: '16:9', visualizer: '16:9', musicVideo: '16:9' };

/**
 * One slot of the Kit tab (design assets-rethink b1 `.ks`): the picture, the name and the state, the
 * lines (the fail reason, "r2 failed. r1 stays.", the Pro line), one binary badge per one-off channel
 * (several files share one badge with a count), and the actions. The long videos and the Canvas take "Add yours" (design
 * music-video a1, a5–a7): the music video has its own actions, an added version says "Added".
 */
const KitSlotCardComponent: React.FC<KitSlotCardProps> = ({
  accountId,
  item,
  view,
  publishes,
  actions,
  onOpen,
  oldCoverUuids,
}) => {
  const intl = useIntl();
  const oldCover = oldCoverUuids !== undefined && view.readyAssets.some((asset) => oldCoverUuids.has(asset.uuid));
  const name = kitSlotName(intl, view.slot.id);
  const channels = useMemo(() => slotPublishes(view, publishes, item.uuid), [item.uuid, publishes, view]);
  const groups = useMemo(() => slotChannelGroups(view, publishes, item.uuid), [item.uuid, publishes, view]);
  const readyUuids = useMemo(() => view.readyAssets.map((asset) => asset.uuid), [view.readyAssets]);
  const lines = useMemo(
    () => kitSlotLines(intl, view, { name, title: item.title, masterFailReason: item.song.masterFailReason ?? null }),
    [intl, item.song.masterFailReason, item.title, name, view]
  );
  const publish = (entry: SlotChannel): void =>
    openPublishDialog({
      accountId,
      kitAssetUuid: entry.asset.uuid,
      catalogItemUuid: item.uuid,
      channel: entry.channel,
      assetName: name,
      downloadUrl: entry.asset.fileUrl === null ? null : downloadMediaUrl(entry.asset.fileUrl),
    });
  // One badge per channel (owner fix 2026-10-10). A manual mark of a slot with several files opens
  // the chooser: one row per file with its own mark, never a mark of every file at once.
  const openGroup = (group: SlotChannelGroup<SlotChannel>): void => {
    const first = group.entries.find((entry) => entry.record?.status !== 'published') ?? group.entries[0];
    if (first === undefined) {
      return;
    }
    if (group.total <= 1 || ONE_OFF_CHANNEL_INFO[group.channel].mode !== 'manual') {
      publish(first);
      return;
    }
    openPublishDialog({
      accountId,
      kitAssetUuid: first.asset.uuid,
      catalogItemUuid: item.uuid,
      channel: group.channel,
      assetName: name,
      files: group.entries.map((entry) => ({
        kitAssetUuid: entry.asset.uuid,
        themeId: entry.asset.themeId,
        variant: entry.asset.variant,
        label: kitOutputLabel(intl, entry.asset),
        downloadUrl: entry.asset.fileUrl === null ? null : downloadMediaUrl(entry.asset.fileUrl),
      })),
    });
  };
  const added = useKitSlotAdded({ accountId, item, view });
  const open = added.thumb.onOpen ?? (view.readyAsset === null ? undefined : () => onOpen(view));
  const isMusicVideo = view.slot.id === 'musicVideo';

  return (
    <div
      className={clsx(
        styles.slot,
        view.state === 'making' && styles.slotWork,
        view.state === 'failed' && styles.slotFail,
        (view.state === 'notMade' || view.state === 'locked') && styles.slotEmpty
      )}
      data-testid={`kit-slot-${view.slot.id}`}
      data-state={view.state}
    >
      <KitSlotThumb
        view={view}
        name={name}
        onOpen={open}
        ghost={FRAME_TEXT[view.slot.id]}
        pictureUrl={added.thumb.pictureUrl}
        badge={added.thumb.badge}
      />
      <div className={styles.body}>
        <div className={styles.name}>
          <b>{name}</b>
          <KitStateChip state={view.state} added={added.canRemove} oldCover={oldCover} />
        </div>
        {lines.map((line) => (
          <span
            key={line.text}
            className={clsx(styles.line, line.bad && styles.lineBad, line.warn === true && styles.lineWarn)}
          >
            {withBrandWord(line.text)}
          </span>
        ))}
        {isMusicVideo && added.source === 'youtube' && view.state === 'made' && actions.canAct && (
          <button type="button" className={clsx(styles.link, 'text-start')} onClick={() => added.openAdd('upload')}>
            {intl.formatMessage(addedAssetsMessages.uploadFileToo)}
          </button>
        )}
        {added.uploadLine !== null && <span className={styles.line}>{added.uploadLine}</span>}
        {view.slot.id === 'linkPreview' && view.state === 'made' && (
          <span className={styles.line}>{intl.formatMessage(kitMessages.linkPreviewLine)}</span>
        )}
        {view.state === 'made' &&
          view.readyAssets
            .filter((asset) => sourceOfAsset(asset) === 'youtube')
            // A linked YouTube video is on YouTube already (design music-video a5): no publish badge.
            .map((asset) => <OnYouTubeBadge key={asset.uuid} youtube={asset.youtube} />)}
        {view.state === 'made' && <PublishBadges groups={groups} canAct={actions.canAct} onOpen={openGroup} />}
        {/* design artist-profile c3: the kit header can become the header of the artist Profile. */}
        {view.slot.id === 'spotifyHeader' && view.state === 'made' && view.readyAsset !== null && (
          <KitHeaderProfileActions
            accountId={accountId}
            catalogItemUuid={item.uuid}
            kitAssetUuid={view.readyAsset.uuid}
            kitAssetUuids={readyUuids}
            canAct={actions.canAct}
          />
        )}
      </div>
      {isMusicVideo ? (
        <KitMusicVideoFoot
          accountId={accountId}
          item={item}
          view={view}
          channels={channels}
          actions={actions}
          onAdd={added.openAdd}
          onOpenEditor={added.openEditor}
          onRemove={added.askRemove}
          onPublish={publish}
          name={name}
          songTitle={item.title}
        />
      ) : (
        <KitSlotFoot
          view={view}
          channels={channels}
          groups={groups}
          actions={actions}
          onOpen={() => onOpen(view)}
          onPublish={publish}
          onMark={openGroup}
          onAddYours={added.themeId === null ? undefined : () => added.openAdd()}
          onRemove={added.canRemove ? added.askRemove : undefined}
          name={name}
          songTitle={item.title}
        />
      )}
      <RemoveAddedAssetDialog
        accountId={accountId}
        catalogItemUuid={item.uuid}
        assetUuid={added.removeUuid}
        name={name}
        onClose={added.closeRemove}
      />
    </div>
  );
};

export const KitSlotCard = TypedMemo(KitSlotCardComponent);
